// Rückruf aus der Inbox (T36): „Verpasster Anruf“, Voicemail und angenommener Anruf werden mit dem Rückruf erledigt.
// Der Anruf startet über die Telefonie; sobald es klingelt oder verbunden ist, wird der Eintrag erledigt (Close). Ein Anruf,
// der gar nicht zustande kommt (Bestätigung nötig, Telefon nicht bereit), lässt den Eintrag offen. Der Wächter hängt im
// App-Rahmen (ErinnerungenHost), damit es auch gilt, wenn man während des Wählens die Seite wechselt.
import { useCallback, useEffect } from "react";
import { aufgabenApi, type Aufgabe } from "../../api";
import { useTelefonie } from "../../telefonie";
import { kopieren } from "../../werkzeuge/tasten";
import { useMeldung } from "../Meldungen";
import { SmsWaechter } from "./SmsWaechter";
import { istAnrufaufgabe, mitArt } from "./typen";

interface Offen {
  taskId: string;
  leadId: string;
  seit: number;
}

let offen: Offen | null = null;
const MAX_WARTEN_MS = 90_000;

/** Merkt, dass der Anruf für diese Aufgabe gerade gestartet wird. */
export function rueckrufMerken(taskId: string, leadId: string): void {
  offen = { taskId, leadId, seit: Date.now() };
}

/** Anrufen: Aufgabe mit Nummer und Lead. Bei Calls (nicht Anrufaufgabe) wird der Eintrag mit dem Rückruf erledigt. */
export function useRueckruf() {
  const tel = useTelefonie();
  const meldung = useMeldung();
  return useCallback(
    async (a: Aufgabe, leadId: string | null = a.lead_id, contactId: string | null = a.contact_id): Promise<void> => {
      const nummer = mitArt(a).phone;
      if (!nummer || !leadId) return;
      if (tel.status === "nicht_eingerichtet") {
        const kopiert = await kopieren(nummer);
        meldung.zeige("info", `${tel.grund ?? "Telefonie ist nicht bereit."}${kopiert ? " Nummer kopiert." : ""}`, { label: "Einstellungen", to: "/einstellungen/telefonie" });
        return;
      }
      // Die Anrufaufgabe erledigt der Server nach einem angenommenen Gespräch, alles andere der Wächter beim Klingeln.
      if (!istAnrufaufgabe(a)) rueckrufMerken(a.id, leadId);
      try {
        await tel.anrufen(nummer, leadId, contactId ?? undefined, { leadName: a.lead_name, kontaktName: a.contact_name });
      } catch (e) {
        offen = null;
        meldung.fehler(e, "Anruf nicht gestartet");
      }
    },
    [tel, meldung],
  );
}

/** Erledigt den Eintrag, sobald der Rückruf klingelt oder läuft. Ohne Anzeige. */
export function RueckrufWaechter() {
  const tel = useTelefonie();
  const meldung = useMeldung();
  useEffect(() => {
    const o = offen;
    if (!o) return;
    if (Date.now() - o.seit > MAX_WARTEN_MS) {
      offen = null;
      return;
    }
    const laeuft = tel.aktiverAnruf?.leadId === o.leadId && (tel.status === "klingelt" || tel.status === "im_gespraech");
    if (laeuft) {
      offen = null;
      aufgabenApi
        .erledigen(o.taskId)
        .then(() => meldung.ok("Rückruf gestartet, Eintrag erledigt."))
        .catch((e) => meldung.fehler(e, "Eintrag nicht erledigt"));
    } else if (tel.fehler) {
      offen = null; // der Anruf kommt nicht zustande oder wartet auf eine Bestätigung
    }
  }, [tel.status, tel.aktiverAnruf, tel.fehler, meldung]);
  return <SmsWaechter />; // Ton bei neuer SMS (6D): ebenfalls ohne Anzeige und im App-Rahmen
}
